import { Logic } from '~common/ui-logic/logic'
import { ExportService } from '~/features/google-api/services/export-service'
import type { AppContextType } from '~/app-context'

export interface ExportButtonDependencies {
    label?: string
    variant?: 'ghost' | 'primary' | 'secondary' | 'outline'
}

export type ExportButtonState = {
    isDropdownOpen: boolean
    isExportModalOpen: boolean
    exportState: 'idle' | 'exporting' | 'success' | 'error'
    exportError?: string
    exportedSpreadsheet?: {
        spreadsheetId: string
        spreadsheetUrl: string
    }
}

export class ExportButtonLogic extends Logic<
    ExportButtonDependencies & AppContextType,
    ExportButtonState
> {
    getInitialState = (): ExportButtonState => ({
        isDropdownOpen: false,
        isExportModalOpen: false,
        exportState: 'idle',
    })

    toggleDropdown(): void {
        this.setState({ isDropdownOpen: !this.state.isDropdownOpen })
    }

    closeDropdown(): void {
        this.setState({ isDropdownOpen: false })
    }

    async selectInvoiceFiles(): Promise<void> {
        await this.openDriveFolder('invoice')
        this.closeDropdown()
    }

    selectCsvFiles(): void {
        this.closeDropdown()
        this.setState({
            isExportModalOpen: true,
            exportState: 'idle',
        })
    }

    selectBankTransactions(): void {
        this.closeExportModal()
        this.deps.services.globalEvents?.emit({
            type: 'openModal',
            modalType: 'export-column-selection',
            metadata: {
                exportType: 'bank-transactions',
            },
        })
    }

    selectInvoices(): void {
        this.closeExportModal()
        this.deps.services.globalEvents?.emit({
            type: 'openModal',
            modalType: 'export-column-selection',
            metadata: {
                exportType: 'invoices',
            },
        })
    }

    async openDriveFolder(fileType?: 'invoice' | 'csv'): Promise<void> {
        const accessToken = this.deps.services.googleApi.getAccessToken()
        if (!accessToken) {
            throw new Error('Not authenticated with Google')
        }

        const user = await this.deps.storageModules.auth.getCurrentUser()
        if (!user) {
            throw new Error('Not authenticated with Google')
        }

        if (!user.googleDriveFolderHierarchy) {
            throw new Error('No Google Drive folder hierarchy found')
        }

        const hierarchyMap = user.googleDriveFolderHierarchy
        const accountEmail = Object.keys(hierarchyMap)[0]
        if (!accountEmail) {
            throw new Error('No Google account found in folder hierarchy')
        }

        const hierarchy = hierarchyMap[accountEmail]
        if (!hierarchy?.rootFolderId) {
            throw new Error('No Google Drive folder hierarchy found')
        }

        // For now, both options open the same root folder
        // This can be extended later to have separate folders for different file types
        const folderId = hierarchy.rootFolderId

        const folderUrl =
            await this.deps.services.googleApi.getGoogleDriveFileUrl(folderId)

        window.open(folderUrl, '_blank')
    }

    closeExportModal(): void {
        this.setState({
            isExportModalOpen: false,
            exportState: 'idle',
            exportError: undefined,
            exportedSpreadsheet: undefined,
        })
    }

    openExportedSpreadsheet(): void {
        if (this.state.exportedSpreadsheet?.spreadsheetUrl) {
            window.open(this.state.exportedSpreadsheet.spreadsheetUrl, '_blank')
        }
    }
}
